import { useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import {
  createReportPdf, preloadPdfLogo, drawSectionBanner, drawKpiTiles, drawCallout,
  drawTable, drawEmptyState, drawClosingFooter, finalizeFooters, savePdf,
  fmtGeneratedAt, severityCell, COLORS,
  type DocCtx, type KpiTile, type TableCell,
} from '../lib/pdfTemplate';
import { fmtTs, getTz } from '../lib/globalTz';

type Lookback = '24h' | '7d' | '30d' | '60d' | '90d';
type DebtSeverity = 'Low' | 'Medium' | 'High' | 'Critical';

interface DebtFinding {
  id: string;
  category: string;
  title: string;
  severity: DebtSeverity;
  count: number;
  scoreImpact: number;
  owner: string;
  evidence: string[];
  recommendation: string;
}

interface QueryTable {
  id: string;
  title: string;
  category: string;
  columns: string[];
  rows: Record<string, unknown>[];
  warnings: string[];
  skipped?: boolean;
}

interface SocDebtPayload {
  ok: true;
  customerId: string;
  customerName: string;
  lookback: Lookback;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  score: { value: number; level: DebtSeverity; summary: string; highestContributors: string[] };
  counters: Record<string, number>;
  findings: DebtFinding[];
  tables: QueryTable[];
  recommendations: Array<{ priority: DebtSeverity; title: string; details: string; owner: string }>;
  diagnostics: string[];
}

interface Props { customerId?: string; customerName?: string; }

function scoreTone(score: number): string {
  if (score >= 85) return 'border-red-300 bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900';
  if (score >= 65) return 'border-orange-300 bg-orange-50 text-orange-800 dark:bg-orange-950/30 dark:text-orange-200 dark:border-orange-900';
  if (score >= 40) return 'border-yellow-300 bg-yellow-50 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-200 dark:border-yellow-900';
  return 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900';
}

function text(value: unknown, max = 160): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function drawAiParagraphs(ctx: DocCtx, markdown: string): void {
  const { pdf } = ctx;
  const lines = markdown.split(/\r?\n/).slice(0, 120);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { ctx.y += 2; continue; }
    if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
    if (line.startsWith('#')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10.5);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(line.replace(/^#+\s*/, ''), 14, ctx.y + 4);
      ctx.y += 7;
      continue;
    }
    const bullet = /^(?:[-*]|\d+\.)\s+(.*)$/.exec(line);
    const printable = bullet ? `• ${bullet[1]}` : line.replace(/\*\*/g, '');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.3);
    pdf.setTextColor(...COLORS.ink);
    const wrapped = pdf.splitTextToSize(printable, 182);
    for (const w of wrapped) {
      if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
      pdf.text(String(w), 14, ctx.y + 4);
      ctx.y += 4.2;
    }
  }
  ctx.y += 2;
}

async function exportPdf(payload: SocDebtPayload, aiAnalysis: string | null, filenameBase: string): Promise<void> {
  await preloadPdfLogo();
  const tz = getTz();
  const ctx = createReportPdf({
    title: 'SOC Debt Dashboard',
    customerName: payload.customerName,
    periodLabel: `${payload.lookback} lookback`,
    generatedLabel: fmtGeneratedAt(payload.generatedAt, tz),
  });

  drawSectionBanner(ctx, 'Debt Overview', 'primary');
  const debtTone: KpiTile['tone'] = payload.score.value >= 85 ? 'danger' : payload.score.value >= 65 ? 'warning' : payload.score.value >= 40 ? 'warning' : 'success';
  const tiles: KpiTile[] = [
    { value: `${payload.score.value}/100`, label: `SOC Debt Score (${payload.score.level})`, tone: debtTone },
    { value: String(payload.counters.stale_incidents ?? 0), label: 'Stale Incidents', tone: 'info' },
    { value: String(payload.counters.recurring_false_positives ?? 0), label: 'Noisy Analytics', tone: 'warning' },
    { value: String(payload.counters.aged_vulnerabilities ?? 0), label: 'Aged Vulnerabilities', tone: 'danger' },
  ];
  drawKpiTiles(ctx, tiles);

  drawCallout(ctx, {
    tone: payload.score.value >= 85 ? 'critical' : payload.score.value >= 65 ? 'warning' : 'success',
    title: `SOC Debt Score: ${payload.score.value}/100 (${payload.score.level})`,
    body: payload.score.summary + (payload.score.highestContributors.length ? ` Highest contributors: ${payload.score.highestContributors.join(', ')}.` : ''),
  });

  if (aiAnalysis) {
    drawSectionBanner(ctx, 'AI Analysis', 'primary');
    drawAiParagraphs(ctx, aiAnalysis.slice(0, 3500));
  }

  drawSectionBanner(ctx, 'Highest Contributors', 'orange');
  if (payload.findings.length) {
    const findingRows: TableCell[][] = payload.findings.slice(0, 15).map((finding) => [
      severityCell(finding.severity),
      text(finding.title, 40),
      String(finding.count),
      String(finding.scoreImpact),
      text(finding.owner, 26),
      text(finding.recommendation, 120),
    ]);
    drawTable(ctx, {
      head: ['Severity', 'Title', 'Count', 'Impact', 'Owner', 'Recommendation'],
      body: findingRows,
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 34 },
        2: { cellWidth: 14, halign: 'center' },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 24 },
        5: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No material SOC debt findings in this lookback.');
  }

  drawSectionBanner(ctx, 'Recommended Actions', 'primary');
  if (payload.recommendations.length) {
    const recRows: TableCell[][] = payload.recommendations.slice(0, 10).map((rec) => [
      severityCell(rec.priority),
      text(rec.title, 40),
      text(rec.details, 140),
      text(rec.owner, 26),
    ]);
    drawTable(ctx, {
      head: ['Priority', 'Title', 'Details', 'Owner'],
      body: recRows,
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 38 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 26 },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No recommended actions for the selected lookback.');
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  savePdf(ctx, filenameBase);
}

export function SocDebtDashboardTab({ customerId, customerName }: Props) {
  const { canCapability } = useAuth();
  const [lookback, setLookback] = useState<Lookback>('30d');
  const [payload, setPayload] = useState<SocDebtPayload | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRead = canCapability('soc_debt', 'read');
  const canRefresh = canCapability('soc_debt', 'refresh');
  const canAi = canCapability('soc_debt', 'ai_analysis');
  const canExport = canCapability('soc_debt', 'export');

  async function load(refresh = false) {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${getApiBase()}/soc-debt/summary?customerId=${encodeURIComponent(customerId)}&lookback=${encodeURIComponent(lookback)}${refresh ? '&refresh=true' : ''}`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) {
        const parsed = await resp.json().catch(() => ({}));
        throw new Error(parsed.message || parsed.error || `SOC Debt load failed (${resp.status})`);
      }
      const data = await resp.json() as SocDebtPayload;
      setPayload(data);
      setAiAnalysis(null);
      setSelectedTableId(data.tables.find((t) => t.rows.length > 0)?.id ?? data.tables[0]?.id ?? '');
      toast({ title: data.cacheHit ? 'SOC Debt loaded from cache' : 'SOC Debt refreshed', description: data.score.summary });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (customerId && canRead) void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  async function generateAiAnalysis() {
    if (!customerId || !payload) return;
    setAiBusy(true);
    setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/soc-debt/ai-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, payload }),
      });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `AI analysis failed (${resp.status})`);
      setAiAnalysis(String(parsed.analysis ?? ''));
      toast({ title: parsed.fallback ? 'SOC Debt fallback analysis generated' : 'SOC Debt AI analysis generated', description: parsed.model ?? 'AI analysis' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAiBusy(false);
    }
  }

  async function exportData(format: 'pdf' | 'csv' | 'html') {
    if (!customerId || !payload) return;
    setExportBusy(format);
    setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/soc-debt/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, payload, format, aiAnalysis }),
      });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Export failed (${resp.status})`);
      const filenameBase = String(parsed.filenameBase || `kloudsecure-soc-debt-${customerId}`);
      if (format === 'csv') downloadText(`${filenameBase}.csv`, String(parsed.csv ?? ''), 'text/csv;charset=utf-8');
      if (format === 'html') downloadText(`${filenameBase}.html`, String(parsed.html ?? ''), 'text/html;charset=utf-8');
      if (format === 'pdf') await exportPdf(payload, aiAnalysis, filenameBase);
      toast({ title: `SOC Debt ${format.toUpperCase()} exported`, description: customerName ?? customerId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExportBusy(null);
    }
  }

  const selectedTable = useMemo(() => payload?.tables.find((table) => table.id === selectedTableId) ?? payload?.tables[0] ?? null, [payload, selectedTableId]);
  const tableColumns = useMemo<FilterableColumnDef<Record<string, unknown>>[]>(() => {
    const columns = selectedTable?.columns?.length ? selectedTable.columns : ['message'];
    return columns.map((column) => ({ key: column, header: column, headerText: column, value: (row) => row[column], render: (row) => text(row[column], 280) }));
  }, [selectedTable]);

  if (!canRead) {
    return <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">SOC Debt Dashboard is hidden by Permissions Master. Enable SOC Debt Dashboard → Read for this role.</div>;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">Operational Governance</div>
            <h2 className="text-2xl font-black text-foreground">SOC Debt Dashboard</h2>
            <p className="text-sm text-muted-foreground max-w-4xl">Tracks accumulated SOC operational debt across stale incidents, unowned work, recurring false positives, MITRE mapping gaps, connector/SOAR health, watchlists, TI expiry, evidence retention, aged vulnerabilities and response playbook hygiene.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Lookback</label>
              <select value={lookback} onChange={(event) => setLookback(event.target.value as Lookback)} className="mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm">
                <option value="24h">24 hours</option><option value="7d">7 days</option><option value="30d">30 days</option><option value="60d">60 days</option><option value="90d">90 days</option>
              </select>
            </div>
            <button type="button" onClick={() => void load(false)} disabled={loading || !customerId} className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60">{loading ? 'Loading…' : 'Load'}</button>
            {canRefresh && <button type="button" onClick={() => void load(true)} disabled={loading || !customerId} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Refresh</button>}
            {canAi && payload && <button type="button" onClick={() => void generateAiAnalysis()} disabled={aiBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">{aiBusy ? 'Analyzing…' : 'AI Analysis'}</button>}
            {canExport && payload && <button type="button" onClick={() => void exportData('pdf')} disabled={!!exportBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">{exportBusy === 'pdf' ? 'Exporting…' : 'Export PDF'}</button>}
            {canExport && payload && <button type="button" onClick={() => void exportData('csv')} disabled={!!exportBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Export CSV</button>}
            {canExport && payload && <button type="button" onClick={() => void exportData('html')} disabled={!!exportBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Export HTML</button>}
          </div>
        </div>
        {payload && <div className="mt-3 text-xs text-muted-foreground">Generated {fmtTs(payload.generatedAt)} · {payload.cacheHit ? 'Loaded from DB cache' : 'Freshly queried'}{payload.expiresAt ? ` · Cache expires ${fmtTs(payload.expiresAt)}` : ''}</div>}
        {error && <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{error}</div>}
      </div>

      {!customerId && <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center text-muted-foreground">Select a customer to load SOC Debt.</div>}

      {payload && (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className={`rounded-2xl border p-5 shadow-sm ${scoreTone(payload.score.value)}`}><div className="text-xs uppercase font-bold">SOC Debt Score</div><div className="mt-2 text-5xl font-black">{payload.score.value}/100</div><div className="font-bold">{payload.score.level}</div></div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="text-xs uppercase text-muted-foreground font-bold">Stale Incidents</div><div className="mt-2 text-4xl font-black text-foreground">{payload.counters.stale_incidents ?? 0}</div><div className="text-xs text-muted-foreground">Open stale backlog</div></div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="text-xs uppercase text-muted-foreground font-bold">Noisy Analytics</div><div className="mt-2 text-4xl font-black text-foreground">{payload.counters.recurring_false_positives ?? 0}</div><div className="text-xs text-muted-foreground">Recurring FP candidates</div></div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="text-xs uppercase text-muted-foreground font-bold">Aged Vulnerabilities</div><div className="mt-2 text-4xl font-black text-foreground">{payload.counters.aged_vulnerabilities ?? 0}</div><div className="text-xs text-muted-foreground">Older than threshold</div></div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h3 className="text-lg font-black text-foreground">Management Summary</h3>
            <p className="mt-2 text-sm text-muted-foreground">{payload.score.summary}</p>
            {aiAnalysis && <div className="mt-4 whitespace-pre-wrap rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-foreground">{aiAnalysis}</div>}
          </div>

          <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-lg font-black text-foreground">Highest Contributors</h3>
              <div className="mt-4 space-y-3">
                {payload.findings.map((finding) => (
                  <div key={finding.id} className="rounded-xl border border-border bg-background p-4">
                    <div className="flex items-center justify-between gap-3"><div className="font-bold text-foreground">{finding.title}</div><span className="rounded-full border border-border px-2 py-0.5 text-xs font-bold">{finding.severity}</span></div>
                    <div className="mt-2 text-xs text-muted-foreground">Count {finding.count} · Score impact {finding.scoreImpact} · Owner {finding.owner}</div>
                    <div className="mt-2 text-sm text-muted-foreground">{finding.recommendation}</div>
                  </div>
                ))}
                {payload.findings.length === 0 && <div className="text-sm text-muted-foreground">No material SOC debt findings in this lookback.</div>}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="text-lg font-black text-foreground">Recommended Actions</h3>
              <div className="mt-4 space-y-3">
                {payload.recommendations.map((rec, index) => <div key={`${rec.title}-${index}`} className="rounded-xl border border-border bg-background p-4"><div className="font-bold text-foreground">{rec.priority} · {rec.title}</div><div className="mt-1 text-sm text-muted-foreground">{rec.details}</div><div className="mt-2 text-xs text-muted-foreground">Owner: {rec.owner}</div></div>)}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div><h3 className="text-lg font-black text-foreground">Debt Detail Tables</h3><p className="text-sm text-muted-foreground">Use the column ... menus for filtering, checklist values and autosize options.</p></div>
              <select value={selectedTableId} onChange={(event) => setSelectedTableId(event.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
                {payload.tables.map((table) => <option key={table.id} value={table.id}>{table.title} ({table.rows.length})</option>)}
              </select>
            </div>
            {selectedTable?.warnings?.length ? <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">{selectedTable.warnings.join(' ')}</div> : null}
            <div className="mt-4">
              <FilterableColumnTable<Record<string, unknown>>
                columns={tableColumns}
                rows={selectedTable?.rows ?? []}
                storageKey={`soc-debt-${customerId}-${selectedTable?.id ?? 'none'}`}
                rowKey={(row, index) => `${selectedTable?.id ?? 'row'}-${index}-${text(row[selectedTable?.columns[0] ?? ''])}`}
                emptyMessage="No rows for this debt category or current filters."
                maxHeightClassName="max-h-[560px]"
              />
            </div>
          </div>

          {payload.diagnostics.length > 0 && <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900"><strong>Partial data warnings:</strong><ul className="mt-2 list-disc pl-5">{payload.diagnostics.slice(0, 8).map((d, i) => <li key={i}>{d}</li>)}</ul></div>}
        </>
      )}
    </div>
  );
}
